import {
  AccordionDetails,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DynamicGrid from "../dynamicGrid/DynamicGrid";
import {
  APP,
  EOsdType,
  ETab,
  EUploadKey,
  FORM_STYLES,
  MAX_FILE_SIZE,
  EXCEPTION_HEADERS_LIST,
  S_GRID_PROPS, XL_GRID_PROPS
} from "../../constants";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { red } from "@mui/material/colors";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { useDispatch, useSelector } from "react-redux";
import { FileUploadAPI } from "../../API/fileUpload";
import { compressImage } from "../../helpers";
import { IUploadFile } from "../../models/master";
import { GetDocTypeAPI } from "../../API/getDocType";
import { saveDocTypesState } from "../../AppState/docTypes";
import AppState from "../../AppState";
import get from "lodash/get";
import Loader from "../loader/loader";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import CustomIconComponent from "../CustomDropdownIcon";
import {
  currentTerminalSelector,
  tabPositionSelector,
} from "../../AppState/selectors";

interface UploadDocumentProps {
  osdNumber?: number;
  uploadKey?: EUploadKey;
  osdType?: string;
  proNumbersList?: number[];
  heading?: { [key: string]: string };
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  osdNumber,
  osdType,
  uploadKey = EUploadKey.od400Pro,
  proNumbersList,
  heading = APP.documentColumns,
}: UploadDocumentProps): JSX.Element => {
  const dispatch = useDispatch();
  const docTypes = useSelector((state: AppState) => state.docTypeMaster);
  const isMSD = osdType === EOsdType.msd;
  const hasNoProNumbers =
    isMSD && (!proNumbersList || proNumbersList.length === 0);
  const [selectedFiles, setSelectedFiles] = useState<IUploadFile[]>([]);
  const [files, setFiles] = useState<File[] | []>([]);
  const [proNumber, setProNumber] = useState<string>("");
  const [selectedDocType, setSelectedDocType] = useState<string>("");
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);

  const activeTerminal = useSelector(currentTerminalSelector)?.terminalId ?? "";
  const tab = useSelector(tabPositionSelector);

  const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
  const allowedPdfType = "application/pdf";

  // 🔹 Helper: Compress images
  const compressLargeImages = async (files: File[]) => {
    const imagesToCompress = files.filter(
      (file) =>
        allowedImageTypes.includes(file.type) && file.size > MAX_FILE_SIZE,
    );

    if (imagesToCompress.length === 0) return files;

    try {
      setPageLoading(true);

      const compressedImages = await compressImage(
        imagesToCompress,
        MAX_FILE_SIZE,
      );

      if (!Array.isArray(compressedImages)) return files;

      return files.map((file) => {
        if (
          allowedImageTypes.includes(file.type) &&
          file.size > MAX_FILE_SIZE
        ) {
          const compressed = compressedImages.find(
            (cf: any) => cf.name === file.name,
          );
          if (compressed) {
            console.log(
              URL.createObjectURL(compressed),
              "Compressed image URL",
            );
          }
          return compressed ?? file;
        }
        return file;
      });
    } catch (error) {
      console.error("Image compression failed:", error);
      return files;
    } finally {
      setPageLoading(false);
    }
  };

  // 🔹 Helper: Validate max file count
  const exceedsFileLimit = (files: File[]) => {
    return files.length + (selectedFiles?.length || 0) > 5;
  };

  // 🔹 Helper: Validate files
  const validateFiles = (files: File[]) => {
    const validFiles: File[] = [];
    const excludedFiles: string[] = [];

    files.forEach((file) => {
      const isValidPdf = file.type === allowedPdfType && file.size <= 4000000;

      const isValidImage =
        allowedImageTypes.includes(file.type) &&
        file.size <= MAX_FILE_SIZE &&
        file.size >= 1000;

      if (isValidPdf || isValidImage) {
        validFiles.push(file);
      } else {
        excludedFiles.push(file.name);
      }
    });

    return { validFiles, excludedFiles };
  };

  // 🔹 Helper: Show excluded files message
  const notifyExcludedFiles = (excludedFiles: string[]) => {
    if (excludedFiles.length === 0) return;

    const isMultiple = excludedFiles.length > 1;

    const message = isMultiple
      ? `Files ${excludedFiles.join(
          ", ",
        )} were not uploaded because their image(s) size is not between 1KB to ${
          MAX_FILE_SIZE / 1024
        }KB or PDF exceeds 4MB or invalid type (${allowedImageTypes.join(
          ", ",
        )}, ${allowedPdfType})`
      : `File ${excludedFiles[0]} was not uploaded because its image size is not between 1KB to ${
          MAX_FILE_SIZE / 1024
        }KB or PDF exceeds 4MB or invalid type (${allowedImageTypes.join(
          ", ",
        )}, ${allowedPdfType})`;

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.INFO,
        message,
        Date.now().toString(),
      ),
    );
  };

  // 🔹 Helper: Update input files
  const updateFileInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    validFiles: File[],
  ) => {
    const dataTransfer = new DataTransfer();
    validFiles.forEach((file) => dataTransfer.items.add(file));
    event.target.files = dataTransfer.files;
  };

  // 🔹 Main function (LOW complexity ✅)
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let files: File[] = event.target.files
      ? Array.from(event.target.files)
      : [];

    files = await compressLargeImages(files);

    if (exceedsFileLimit(files)) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.INFO,
          "You can only select up to 5 files, including existing ones",
          Date.now().toString(),
        ),
      );
      event.target.value = "";
      return;
    }

    const { validFiles, excludedFiles } = validateFiles(files);

    notifyExcludedFiles(excludedFiles);

    updateFileInput(event, validFiles);

    setFiles(validFiles);
  };

  const rowCols: string[] = Object.keys(heading);
  // Configure columns and add manually style and
  const columns: GridColDef[] = rowCols.map((ele: string) => {
    //customized overages grid columns like width, add custom links etc.
    let row: GridColDef = {
      field: ele,
      headerName: heading[ele],
      description: heading[ele],
      type: "string",
      editable: false,
      sortable: false,
      flex: 1,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    };

    if (["Doc Type", "Delete"].includes(heading[ele])) {
      row["minWidth"] = 30;
    } else if (heading[ele] === "File Name") {
      row["minWidth"] = 120;
    }

    if (ele === "id") {
      row["renderCell"] = (params: GridRenderCellParams) => (
        <div>
          <DeleteOutlineIcon
            style={{ color: red[500], cursor: "pointer" }}
            className="blue-color"
            onClick={() => {
              onDelete(params.row["id"]);
            }}
            to={""}
          />
        </div>
      );
    }
    return row;
  });

  const onDelete = (id: string) => {
    const newSelectedFiles = selectedFiles.filter(
      (file: IUploadFile) => file.id !== id,
    );
    setSelectedFiles(newSelectedFiles);
  };

  const handleDocTypeChange = (docType: string) => {
    setSelectedDocType(docType);
  };

  const AddImages = () => {
    const newRows = files.map((file: File, index: number) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      docType: selectedDocType,
      proNumber: isMSD ? Number(proNumber) : undefined,
      file: file,
    }));
    setSelectedFiles([...selectedFiles, ...newRows]);
    setFiles([]);
    setSelectedDocType("");
    setProNumber("");
  };

  const upload = async () => {
    const uploadPromises = selectedFiles.map((fileObj: IUploadFile) => {
      const reqInput: IUploadFile = {
        osdNumber: osdNumber,
        proNumber: isMSD ? Number(fileObj.proNumber) : undefined,
        docType: fileObj.docType,
        uploadKey: uploadKey,
      };

      const headers: { [key: string]: string } =
        EXCEPTION_HEADERS_LIST[tab as keyof typeof EXCEPTION_HEADERS_LIST] ||
        {};

      const terminalId = activeTerminal;
      if (terminalId) {
        if ([ETab.overages].includes(tab)) {
          headers["OSD-Reporting-Terminal"] = terminalId;
        } else if ([ETab.misdelivery].includes(tab)) {
          headers["OSD-Pickup-Terminal"] = terminalId;
        } else if (![ETab.myAssignments, ETab.dashboard].includes(tab)) {
          headers["OSD-Destination-Terminal"] = terminalId;
        }
      }
      headers["OSD-Exception-Type"] = osdType ?? "";

      return FileUploadAPI([fileObj.file!], reqInput, headers);
    });

    try {
      setPageLoading(true);
      const uploadDocResult = await Promise.all(uploadPromises);

      // Check for errors in the response
      const allErrors = uploadDocResult.flatMap((res) =>
        get(res, "errors", []),
      );
      const hasErrors = allErrors.length > 0;

      const notificationType = hasErrors
        ? NOTIFICATION_TYPE.ERROR
        : NOTIFICATION_TYPE.SUCCESS;
      const message = hasErrors
        ? allErrors.join(", ")
        : uploadDocResult[0].result || "Document(s) successfully uploaded";

      // Clear the files and update state
      setSelectedFiles([]);
      setFiles([]);
      dispatch(isFormValueChanged(false));

      // Dispatch notification
      dispatch(
        PopoutNotification(notificationType, message, Date.now().toString()),
      );
    } catch (error) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : `Failed document(s) uploading`,
          Date.now().toString(),
        ),
      );
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    const getDocType = async () => {
      try {
        const response = await GetDocTypeAPI();
        dispatch(saveDocTypesState(get(response, "data", [])));
      } catch (error) {
        console.error(error);
      }
    };
    if (
      !Array.isArray(docTypes) ||
      (Array.isArray(docTypes) && docTypes?.length < 1)
    ) {
      getDocType();
    }
    dispatch(isFormValueChanged(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (files.length > 0) {
      if (
        selectedDocType &&
        selectedFiles?.length < 6 &&
        (!isMSD || (isMSD && proNumber))
      ) {
        AddImages();
      }
      dispatch(isFormValueChanged(true));
    } else if (selectedDocType || selectedFiles.length > 0) {
      dispatch(isFormValueChanged(true));
    } else {
      dispatch(isFormValueChanged(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, selectedDocType, selectedFiles, proNumber]);

  return (
    <div data-testid="upload-document">
      <AccordionDetails>
        <Grid container spacing={2} className="filter-margin">
          {isMSD && (
            <Grid item {...S_GRID_PROPS}>
              <FormControl fullWidth size="small">
                <InputLabel
                  style={{ fontSize: "13px" }}
                  shrink={true}
                  id="proNumber"
                >
                  PRO # <sup>*</sup>
                </InputLabel>
                <Select
                  variant="outlined"
                  id="proNumber"
                  placeholder="Select a PRO Number"
                  className="select"
                  value={proNumber}
                  fullWidth
                  disabled={selectedFiles?.length >= 5 || hasNoProNumbers}
                  onChange={(e) => setProNumber(e.target.value)}
                  style={FORM_STYLES.LABEL}
                  IconComponent={CustomIconComponent}
                  displayEmpty
                  renderValue={(selected) => {
                    if (proNumber.length === 0) {
                      return <em>Select Pro #</em>;
                    }
                    return selected;
                  }}
                >
                  {proNumbersList?.map((obj: number) => (
                    <MenuItem style={FORM_STYLES.LABEL} key={obj} value={obj}>
                      {obj}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="file">
                Upload Documents or Capture Image (Max 5 files)<sup>&nbsp;</sup>
              </InputLabel>
              <TextField
                autoComplete="off"
                id="docType"
                placeholder="Select Files"
                variant="outlined"
                inputProps={{
                  multiple: true,
                  accept: ".jpg,.jpeg,.png,.pdf",
                  "data-testid": "nativeFileInput",
                }}
                name=" "
                type="file"
                data-testid="fileInput"
                disabled={selectedFiles?.length >= 5 || hasNoProNumbers}
                key={selectedFiles.length}
                onChange={handleFileChange}
              />
            </FormControl>
          </Grid>

          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="type">
                Doc Type <sup>&nbsp;</sup>
              </InputLabel>
              <Select
                variant="outlined"
                disabled={selectedFiles?.length >= 5 || hasNoProNumbers}
                id="fileType"
                className="select"
                fullWidth
                onChange={(e) => handleDocTypeChange(e.target.value)}
                name="fileType"
                value={selectedDocType}
                data-testid="docTypeSelect"
                style={FORM_STYLES.LABEL}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Select Doc Type</em>;
                  }
                  return selected;
                }}
                IconComponent={CustomIconComponent}
              >
                {docTypes?.map((docType: string) => (
                  <MenuItem
                    style={FORM_STYLES.LABEL}
                    key={docType}
                    value={docType}
                  >
                    {docType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {hasNoProNumbers && (
            <Grid item {...XL_GRID_PROPS}>
              <strong>
                Document upload is not possible because no PRO numbers are
                available.
              </strong>
            </Grid>
          )}
        </Grid>
        {selectedFiles?.length > 0 && (
          <Grid container spacing={2} className="filter-margin">
            <Grid item md={isMSD ? 9 : 6} xs={12}>
              <DynamicGrid
                id="id"
                key={selectedFiles?.map((file) => file.id).join(",")}
                checkboxSelection={false}
                rows={selectedFiles}
                columns={columns}
                hideFooter={true}
                disableColumnMenu={true}
                paginationMode={"client"}
              ></DynamicGrid>
            </Grid>
          </Grid>
        )}

        <div className="btn-group-submit">
          <Button
            variant="contained"
            type="button"
            className={
              selectedFiles.length === 0
                ? "disable-btn disable assign mr-0 ml"
                : "assign mr-0 ml"
            }
            onClick={() => {
              selectedFiles.length === 0 ? void 0 : upload();
            }}
          >
            Upload
          </Button>
        </div>
      </AccordionDetails>

      <Loader pageLoader={pageLoading}></Loader>
    </div>
  );
};

export default UploadDocument;
